import React, { createContext, useState, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

// Extend the type definition to include new fields and their setter functions
type AlertsContextType = {
  videoUrl: string;
  setVideoUrl: (newUrl: string) => void;
  eventTypes: string[];
  setEventTypes: (newEventTypes: string[]) => void;
  siteIds: string[];
  setSiteIds: (newSiteIds: string[]) => void;
  cameraIds: string[];
  setCameraIds: (newCameraIds: string[]) => void;
  startedAt: string | Date;
  setStartedAt: (newStartedAt: string | Date) => void;
  endedAt: string | Date;
  setEndedAt: (newEndedAt: string | Date) => void;
  read: string;
  setRead: (newRead: string) => void;
  statuses: string[];
  setStatuses: (newStatuses: string[]) => void;
  personCoatColors: string[];
  setPersonCoatColors: (newPersonCoatColors: string[]) => void;
  personTrousersColors: string[];
  setPersonTrousersColors: (newPersonTrousersColors: string[]) => void;
  vehicleColors: string[];
  setVehicleColors: (newVehicleColors: string[]) => void;
  nonMotorVehiclePersonCoatColors: string[];
  setNonMotorVehiclePersonCoatColors: (
    newNonMotorVehiclePersonCoatColors: string[],
  ) => void;
  vehiclePlateNos: string[];
  setVehiclePlateNos: (newVehiclePlateNos: string[]) => void;
  gptText: string;
  setGptText: (newGptText: string) => void;
  vehicleSearchParams: any;
  setVehicleSearchParams: (newVehicleSearchParams: any) => void;
  searchType: string;
  setSearchType: (newSearchType: string) => void;
};

// Update the initial value to include new fields
const initialValue: AlertsContextType = {
  videoUrl: "",
  setVideoUrl: () => {},
  eventTypes: [],
  setEventTypes: () => {},
  siteIds: [],
  setSiteIds: () => {},
  cameraIds: [],
  setCameraIds: () => {},
  startedAt: "",
  setStartedAt: () => {},
  endedAt: "",
  setEndedAt: () => {},
  read: "false",
  setRead: () => {},
  statuses: [],
  setStatuses: () => {},
  personCoatColors: [],
  setPersonCoatColors: () => {},
  personTrousersColors: [],
  setPersonTrousersColors: () => {},
  vehicleColors: [],
  setVehicleColors: () => {},
  nonMotorVehiclePersonCoatColors: [],
  setNonMotorVehiclePersonCoatColors: () => {},
  vehiclePlateNos: [],
  setVehiclePlateNos: () => {},
  gptText: "",
  setGptText: () => {},
  vehicleSearchParams: {},
  setVehicleSearchParams: () => {},
  searchType: "magic",
  setSearchType: () => {},
};

// Create the context with the updated initial value
export const AlertsContext = createContext<AlertsContextType>(initialValue);

// Update the provider component to manage and provide the new state properties
export default function AlertsProvider({ children }: { children?: ReactNode }) {
  const [searchType, setSearchType] = useState<string>("magic");
  const [searchParams, setSearchParams] = useSearchParams();
  const queryStartedAt = searchParams.get("startedAt");
  const queryEndedAt = searchParams.get("endedAt");

  const [videoUrl, setVideoUrl] = useState<string>(initialValue.videoUrl);
  const [eventTypes, setEventTypes] = useState<string[]>(
    initialValue.eventTypes,
  );
  const [siteIds, setSiteIds] = useState<string[]>(initialValue.siteIds);
  const [cameraIds, setCameraIds] = useState<string[]>(initialValue.cameraIds);
  const [startedAt, setStartedAt] = useState<Date | string>(
    queryStartedAt ? new Date(Number(queryStartedAt)) : initialValue.startedAt,
  );
  const [endedAt, setEndedAt] = useState<Date | string>(
    queryEndedAt ? new Date(Number(queryEndedAt)) : initialValue.endedAt,
  );
  const [read, setRead] = useState<string>(initialValue.read);
  const [statuses, setStatuses] = useState<string[]>(initialValue.statuses);
  const [personCoatColors, setPersonCoatColors] = useState<string[]>(
    initialValue.personCoatColors,
  );
  const [personTrousersColors, setPersonTrousersColors] = useState<string[]>(
    initialValue.personTrousersColors,
  );
  const [vehicleColors, setVehicleColors] = useState<string[]>(
    initialValue.vehicleColors,
  );
  const [nonMotorVehiclePersonCoatColors, setNonMotorVehiclePersonCoatColors] =
    useState<string[]>(initialValue.nonMotorVehiclePersonCoatColors);
  const [vehiclePlateNos, setVehiclePlateNos] = useState<string[]>(
    initialValue.vehiclePlateNos,
  );
  const [gptText, setGptText] = useState<string>(initialValue.gptText);
  const [vehicleSearchParams, setVehicleSearchParams] = useState({});
  // Provide all state and setter functions through context
  return (
    <AlertsContext.Provider
      value={{
        videoUrl,
        setVideoUrl,
        eventTypes,
        setEventTypes,
        siteIds,
        setSiteIds,
        cameraIds,
        setCameraIds,
        startedAt,
        setStartedAt,
        endedAt,
        setEndedAt,
        read,
        setRead,
        statuses,
        setStatuses,
        personCoatColors,
        setPersonCoatColors,
        personTrousersColors,
        setPersonTrousersColors,
        vehicleColors,
        setVehicleColors,
        nonMotorVehiclePersonCoatColors,
        setNonMotorVehiclePersonCoatColors,
        vehiclePlateNos,
        setVehiclePlateNos,
        gptText,
        setGptText,
        vehicleSearchParams,
        setVehicleSearchParams,
        searchType,
        setSearchType,
      }}
    >
      {children}
    </AlertsContext.Provider>
  );
}
